import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from 'react';
import axios from 'axios';
import { useCurrentUser } from './CurrentUserProvider.jsx';
import { CurrentHotelContext } from '../contexts/CurrentHotelContext.jsx';
import { useLocation } from 'react-router-dom';
import {apiEndpoints} from '../utils/apiEndpoints.js';

const dataLoadingStatus = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  error: 'error',
};

export const CurrentHotelProvider = ({ initHotel, children }) => {
  const [currentHotel, setCurrentHotel] = useState(initHotel ?? null);
  const [loadingState, setLoadingState] = useState(
    initHotel ? dataLoadingStatus.success : dataLoadingStatus.idle
  );
  const [error, setError] = useState(null);
  const {currentUser, isLoading: isUserLoading} = useCurrentUser();

  const isLegacy = !window.location.pathname.startsWith('/app');
  const location = isLegacy ? window.location : useLocation();
  const interceptorIdRef = useRef(null);

  const currentHotelId = useMemo(() => {
    if (isLegacy) {
      return currentUser?.currentHotelId
    }

    const pathParts = location.pathname.split('/');
    const hotelIdParam = parseInt(pathParts[1], 10)

    return !hotelIdParam ? null : hotelIdParam
  }, [currentUser, location.pathname])

  const loadCurrentHotel = useCallback(async () => {
    if (!currentHotelId) {
      setCurrentHotel(null);
      setLoadingState(dataLoadingStatus.success);
      setError(null);
      return;
    }

    setLoadingState(dataLoadingStatus.loading);
    setError(null);

    try {
      const response = await axios.get(`${apiEndpoints.hotels}/${currentHotelId}`);
      setCurrentHotel(response.data);
      setLoadingState(dataLoadingStatus.success);
      return response.data;
    } catch (err) {
      console.error('Failed to load current hotel:', err);
      setError(err?.response?.data?.message || 'Failed to load hotel');
      setLoadingState(dataLoadingStatus.error);
      setCurrentHotel(null);
    }
  }, [currentHotelId]);

  const linkTo = useCallback(
    (path) => {
      return `/${currentHotelId ? currentHotelId : 'all'}/${path}`;
    },
    [currentHotelId]
  );

  useEffect(() => {
    if (initHotel) {
      return;
    }
    if (isUserLoading) {
      return;
    }
    loadCurrentHotel();
  }, [currentUser?.currentHotelId, isUserLoading, loadCurrentHotel, initHotel]);

  useLayoutEffect(() => {
    if (interceptorIdRef.current !== null) {
      axios.interceptors.request.eject(interceptorIdRef.current);
      interceptorIdRef.current = null;
    }

    interceptorIdRef.current = axios.interceptors.request.use((config) => {
      const params = { ...(config.params || {}) };

      if (currentHotelId) {
        params.hotelId = currentHotelId;
      } else {
        delete params.hotelId;
      }

      return { ...config, params };
    });

    // Cleanup
    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [currentHotelId]);

  const value = useMemo(() => ({
    currentHotel,
    loadingState,
    currentHotelId,
    isLoading: loadingState === dataLoadingStatus.loading,
    isHotelLoading: loadingState === dataLoadingStatus.loading,
    hasHotel: !!currentHotel,
    error,
    loadCurrentHotel,
    setCurrentHotel,
    linkTo,
  }), [currentHotelId, currentHotel, loadingState, error, loadCurrentHotel, linkTo]);

  return (
    <CurrentHotelContext.Provider value={value}>
      {children}
    </CurrentHotelContext.Provider>
  );
};

export const useCurrentHotel = () => {
  const context = useContext(CurrentHotelContext);
  if (!context) {
    throw new Error('useCurrentHotel must be used within a CurrentHotelProvider');
  }
  return context;
};
